import {
    collection,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
} from 'firebase/firestore';
// Extensie is verplicht: package.json staat op "type": "module", dus .js-bestanden
// worden als strikte ESM behandeld.
import { db } from '../firebase.js';

/**
 * Data-toegang voor seizoenen.
 *
 * Let op: het document-id van een seizoen is de Sportmonks season-id. Daar keyen
 * `auctions.season`, `schedule/{seasonId}`, `playerScores/{seasonId}` en
 * `players.seasonId` allemaal op — het id mag dus nooit veranderen.
 */

export const SEASON_STATUS = {
    VOORBEREIDING: 'voorbereiding',
    ACTIEF: 'actief',
    AFGEROND: 'afgerond',
};

export const SEASON_STATUS_LABELS = {
    [SEASON_STATUS.VOORBEREIDING]: 'Voorbereiding',
    [SEASON_STATUS.ACTIEF]: 'Actief',
    [SEASON_STATUS.AFGEROND]: 'Afgerond',
};

/**
 * Bestaande seizoensdocumenten hebben alleen `name` en `importDate`. Zolang het
 * veld `status` er niet op staat leiden we het af, zodat de UI werkt zonder backfill:
 * het nieuwste seizoen met een kalender is actief, oudere seizoenen zijn afgerond,
 * en een seizoen zonder kalender staat nog in voorbereiding.
 */
function deriveStatus(season, { hasCalendar, isNewest }) {
    if (season.status) return season.status;
    if (!hasCalendar) return SEASON_STATUS.VOORBEREIDING;
    return isNewest ? SEASON_STATUS.ACTIEF : SEASON_STATUS.AFGEROND;
}

/** Haalt één seizoen op, inclusief afgeleide velden. Geeft null als het niet bestaat. */
export async function fetchSeason(seasonId) {
    const snap = await getDoc(doc(db, 'seasons', String(seasonId)));
    if (!snap.exists()) return null;

    const data = { id: snap.id, ...snap.data() };
    const roundCount = await fetchRoundCount(snap.id);

    return {
        ...data,
        displayName: data.displayName || `Champions League ${data.name || snap.id}`,
        status: deriveStatus(data, { hasCalendar: roundCount > 0, isNewest: true }),
        roundCount,
    };
}

/** Alle seizoenen, nieuwste eerst. */
export async function fetchSeasons() {
    const snapshot = await getDocs(query(collection(db, 'seasons'), orderBy('name', 'desc')));
    const raw = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    const roundCounts = await Promise.all(raw.map((s) => fetchRoundCount(s.id)));

    // Het nieuwste seizoen mét kalender geldt als het actieve seizoen.
    const newestWithCalendarIndex = roundCounts.findIndex((count) => count > 0);

    return raw.map((season, index) => ({
        ...season,
        displayName: season.displayName || `Champions League ${season.name || season.id}`,
        roundCount: roundCounts[index],
        status: deriveStatus(season, {
            hasCalendar: roundCounts[index] > 0,
            isNewest: index === newestWithCalendarIndex,
        }),
    }));
}

/** Aantal geïmporteerde speelrondes in de globale kalender van dit seizoen. */
async function fetchRoundCount(seasonId) {
    try {
        const snapshot = await getDocs(collection(db, 'schedule', String(seasonId), 'rounds'));
        return snapshot.size;
    } catch (error) {
        console.error(`Kon rondes voor seizoen ${seasonId} niet ophalen:`, error);
        return 0;
    }
}

/**
 * Aantal subleagues en deelnemers per seizoen, uit één query over alle veilingen.
 * Er zijn er tientallen, geen duizenden — client-side groeperen is hier goedkoper
 * dan denormaliseren of per seizoen een aparte query doen.
 *
 * @returns {Promise<Record<string, {subleagueCount: number, participantCount: number}>>}
 */
export async function fetchAuctionTotalsBySeason() {
    const snapshot = await getDocs(collection(db, 'auctions'));
    const totals = {};

    snapshot.docs.forEach((d) => {
        const data = d.data();
        const seasonId = data.season;
        if (!seasonId) return;

        if (!totals[seasonId]) {
            totals[seasonId] = { subleagueCount: 0, participantCount: 0 };
        }
        totals[seasonId].subleagueCount += 1;
        totals[seasonId].participantCount += (data.participantUids || []).length;
    });

    return totals;
}

/** Alle subleagues (veilingen) van één seizoen. */
export async function fetchSubleaguesForSeason(seasonId) {
    const snapshot = await getDocs(collection(db, 'auctions'));
    return snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((auction) => auction.season === String(seasonId))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** Schrijft velden weg op het seizoensdocument. */
export async function updateSeason(seasonId, fields) {
    await updateDoc(doc(db, 'seasons', String(seasonId)), {
        ...fields,
        updatedAt: serverTimestamp(),
    });
}

/** De standaardinstellingen voor het automatisch ophalen van statistieken. */
export const DEFAULT_STATS_SYNC = {
    enabled: false,
    intervalMinutes: 3,
    window: 'matchdays',
    hoursAfterFinish: 2,
};

/** De instellingen van dit seizoen, aangevuld met de standaarden. */
export function readStatsSync(season) {
    return { ...DEFAULT_STATS_SYNC, ...(season?.statsSync || {}) };
}

/**
 * Schrijft de sync-instellingen weg met puntpaden, zodat `lastRunAt` en
 * `lastRunSummary` van de planner blijven staan. `nextRunAt` wordt juist gewist:
 * na een wijziging mag de planner meteen opnieuw kijken.
 */
export async function updateStatsSyncSettings(seasonId, settings) {
    await updateDoc(doc(db, 'seasons', String(seasonId)), {
        'statsSync.enabled': settings.enabled,
        'statsSync.intervalMinutes': settings.intervalMinutes,
        'statsSync.window': settings.window,
        'statsSync.hoursAfterFinish': settings.hoursAfterFinish,
        'statsSync.nextRunAt': null,
        updatedAt: serverTimestamp(),
    });
}
